import React, { useEffect, useState } from 'react';
import { useProduct } from '../../../hooks/useProduct';
import InfoField from '../../../components/Shared/InfoField';
import SectionInfoCard from '../../FormSections/SectionInfoCard';
import FormModal from '../../Modals/FormModal';

interface ProductViewPopupProps {
  popupOpen: boolean;
  onClose: () => void;
}

const ProductViewPopup: React.FC<ProductViewPopupProps> = ({
  popupOpen,
  onClose,
}) => {
  const { form, handlerGetProductImage } = useProduct();
  const [productImageUrl, setProductImageUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchImage = async () => {
      if (form?.imagePath) {
        const url = await handlerGetProductImage(form.imagePath);
        setProductImageUrl(url);
      }
    };
    fetchImage();
  }, [form]);

  const isService = form.itemTypeCode === 2;

  return (
    <FormModal
      isOpen={popupOpen}
      onClose={onClose}
      title="Detalle del Producto"
      maxWidth="max-w-3xl"
    >
      {/* Encabezado visual */}
      <div className="mb-4 flex flex-col items-center gap-4 sm:flex-row sm:items-start">
        {productImageUrl && (
          <div className="h-32 w-32 overflow-hidden rounded-lg border border-stroke shadow-md dark:border-strokedark">
            <img
              src={productImageUrl}
              alt="Imagen del producto"
              className="h-full w-full object-cover"
            />
          </div>
        )}

        <div className="flex-1 text-center sm:text-left">
          <h2 className="text-2xl font-bold text-black dark:text-white">
            {form.name}
          </h2>

          <p className="mt-1 text-sm font-medium text-bodydark2 dark:text-bodydark">
            <strong>Código:</strong> {form.code}
          </p>

          <div className="mt-3 flex items-center justify-center gap-2 sm:justify-start">
            <span className="text-sm font-semibold text-black dark:text-white">
              Estado:
            </span>

            <span
              className={`inline-flex rounded-md px-3 py-1 text-sm font-medium ${
                form.status
                  ? 'bg-[#3CA745] text-white'
                  : 'bg-[#DC3545] text-white'
              }`}
            >
              {form.status ? 'Activo' : 'Inactivo'}
            </span>
          </div>
        </div>
      </div>

      {/* Sección: General */}
      <SectionInfoCard
        title="Información General"
        defaultOpen={true}
        columns={2}
      >
        <InfoField label="Descripción" value={form.description || 'N/A'} />

        <InfoField
          label="Precio"
          value={form.price != null ? `$${form.price.toFixed(2)}` : '—'}
        />

        <InfoField
          label="Precio Mínimo"
          value={
            form.minimumSalePrice != null
              ? `$${form.minimumSalePrice.toFixed(2)}`
              : '—'
          }
        />

        {!isService && (
          <>
            <InfoField
              label="Costo"
              value={form.cost != null ? `$${form.cost.toFixed(2)}` : '—'}
            />

            <InfoField
              label="Stock"
              value={form.stock != null ? form.stock.toString() : '—'}
            />
          </>
        )}

        <InfoField label="Unidad de Medida" value={form.unitName || '—'} />
        <InfoField label="Categoría" value={form.categoryName || '—'} />
        <InfoField label="Subcategoría" value={form.subCategoryName || '—'} />
        <InfoField label="Tipo" value={form.itemTypeDescription || '—'} />
      </SectionInfoCard>

      <SectionInfoCard title="Cargos adicionales" columns={2}>
        <InfoField
          label="Aplica propina"
          value={form.tipEligible ? 'Sí' : 'No'}
        />
        <InfoField
          label="Aplica turismo 5 %"
          value={form.appliesTourismTax ? 'Sí' : 'No'}
        />
      </SectionInfoCard>

      {/* Clasificación */}
      <SectionInfoCard
        title="Clasificación"
        message="Organización comercial del producto"
        columns={2}
      >
        <InfoField label="Marca" value={form.brandName || '—'} />
        <InfoField label="Subcategoría" value={form.subCategoryName || '—'} />
      </SectionInfoCard>

      {/* Identificadores externos */}
      <SectionInfoCard title="Identificadores Externos" columns={2}>
        <InfoField label="Barcode" value={form.barcode || '—'} />
        <InfoField label="SKU" value={form.sku || '—'} />
        <InfoField label="Fabricante" value={form.manufacturerCode || '—'} />
        <InfoField label="Código Búsqueda" value={form.searchCode || '—'} />
      </SectionInfoCard>

      {/* Trazabilidad */}
      <SectionInfoCard
        title="Trazabilidad"
        message="Control de lotes y vencimientos"
        columns={2}
      >
        <InfoField label="Lote" value={form.lotNumber || '—'} />
        <InfoField label="Batch" value={form.batch || '—'} />
        <InfoField label="Vencimiento" value={form.expirationDate || '—'} />
      </SectionInfoCard>

      {/* Variantes */}
      <SectionInfoCard
        title="Atributos / Variantes"
        message="Color, talla y particularidades"
        columns={2}
      >
        <InfoField label="Color" value={form.colorName || '—'} />
        <InfoField label="Talla" value={form.sizeName || '—'} />
        <InfoField label="Variante" value={form.variant || '—'} />
      </SectionInfoCard>

      {/* Dimensiones */}
      <SectionInfoCard
        title="Dimensiones"
        message="Información útil para logística"
        columns={2}
      >
        <InfoField label="Peso (kg)" value={form.weight ?? '—'} />
        <InfoField label="Altura (cm)" value={form.height ?? '—'} />
        <InfoField label="Ancho (cm)" value={form.width ?? '—'} />
        <InfoField label="Largo (cm)" value={form.length ?? '—'} />
      </SectionInfoCard>

      {/* Condiciones especiales */}
      <SectionInfoCard
        title="Condiciones Especiales"
        message="Requerimientos de almacenamiento"
        columns={2}
      >
        <InfoField
          label="Sensibilidad Humedad"
          value={form.humiditySensitive ? 'Sí' : 'No'}
        />

        <InfoField label="Cadena Frío" value={form.coldChain ? 'Sí' : 'No'} />

        <InfoField label="Consumible" value={form.consumable ? 'Sí' : 'No'} />
      </SectionInfoCard>

      {/* Ubicación física */}
      <SectionInfoCard
        title="Ubicación Física en Almacén"
        message="Donde se encuentra el producto"
        columns={2}
      >
        <InfoField label="Pasillo" value={form.storageAisle || '—'} />
        <InfoField label="Fila" value={form.storageRow || '—'} />
        <InfoField label="Estante" value={form.storageShelf || '—'} />
        <InfoField label="Gaveta" value={form.storageBin || '—'} />
      </SectionInfoCard>

      <button
        onClick={onClose}
        className="mt-6 w-full rounded-md bg-primary px-4 py-2 text-white hover:bg-opacity-90"
      >
        Cerrar
      </button>
    </FormModal>
  );
};

export default ProductViewPopup;
